import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import {
  FiPlus,
  FiEdit2,
  FiTrash2,
  FiEye,
  FiEyeOff,
  FiCalendar,
  FiSearch,
  FiUpload,
  FiX,
  FiImage,
} from "react-icons/fi";
import { resolveImageUrl } from "../../utils/helpers";
import { toast } from "react-toastify";
import { eventAPI } from "../../services/api";
import { formatDate } from "../../utils/helpers";
import { triggerRefresh } from "../../hooks/useDataRefresh";
import AdminLayout from "../../components/layout/AdminLayout";
import Modal from "../../components/common/Modal";
import LoadingSpinner from "../../components/common/LoadingSpinner";

const CATEGORIES = [
  "Academic",
  "Sports",
  "Cultural",
  "Meeting",
  "Holiday",
  "Other",
];

const emptyForm = {
  title: "",
  description: "",
  date: "",
  endDate: "",
  location: "",
  category: "Academic",
  isPublished: true,
};

const AdminEvents = () => {
  const [events, setEvents] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modal, setModal] = useState(false);
  const [bulkImageModal, setBulkImageModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [bulkImageFiles, setBulkImageFiles] = useState([]);
  const [bulkImagePreviews, setBulkImagePreviews] = useState([]);
  const [bulkImageSaving, setBulkImageSaving] = useState(false);
  const [selectedEventForBulk, setSelectedEventForBulk] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await eventAPI.getAdmin();
        const data = res.data?.data?.events || [];
        setEvents(data);
        setFiltered(data);
      } catch (err) {
        toast.error("Failed to load events");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  useEffect(() => {
    const result = search.trim()
      ? events.filter((e) =>
          e.title.toLowerCase().includes(search.toLowerCase()),
        )
      : events;
    setFiltered(result);
  }, [search, events]);

  const openCreate = () => {
    setEditItem(null);
    setForm(emptyForm);
    setImageFile(null);
    setImagePreview(null);
    setModal(true);
  };
  const openEdit = (item) => {
    setEditItem(item);
    setForm({
      title: item.title,
      description: item.description,
      date: item.date?.slice(0, 10) || "",
      endDate: item.endDate?.slice(0, 10) || "",
      location: item.location || "",
      category: item.category,
      isPublished: item.isPublished,
    });
    setImageFile(null);
    setImagePreview(item.image?.url ? resolveImageUrl(item.image.url) : null);
    setModal(true);
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setImageFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setImagePreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleBulkImageChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setBulkImageFiles(files);

    // Create previews for all selected files
    const previews = [];
    let loadedCount = 0;

    files.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (ev) => {
        previews.push({
          file,
          preview: ev.target.result,
        });
        loadedCount++;
        if (loadedCount === files.length) {
          setBulkImagePreviews(previews);
        }
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSave = async () => {
    if (!form.title.trim() || !form.date) {
      toast.error("Title and date are required");
      return;
    }
    if (!form.description.trim()) {
      toast.error("Description is required");
      return;
    }
    setSaving(true);
    try {
      const payload = imageFile
        ? (() => {
            const fd = new FormData();
            Object.entries(form).forEach(([k, v]) => fd.append(k, v));
            fd.append("image", imageFile);
            return fd;
          })()
        : form;

      if (editItem) {
        const res = await eventAPI.update(editItem._id, payload);
        const updated = res.data?.data?.event || { ...editItem, ...form };
        setEvents((prev) =>
          prev.map((e) => (e._id === editItem._id ? updated : e)),
        );
        toast.success("Event updated");
        triggerRefresh("events"); // Update all events sections
      } else {
        const res = await eventAPI.create(payload);
        const newItem = res.data?.data?.event;
        if (newItem) setEvents((prev) => [newItem, ...prev]);
        toast.success("Event created");
        triggerRefresh("events"); // Update all events sections
      }
      setModal(false);
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to save event");
    } finally {
      setSaving(false);
    }
  };

  const handleBulkImageUpload = async () => {
    if (!selectedEventForBulk || bulkImageFiles.length === 0) {
      toast.error("Please select an event and images");
      return;
    }
    setBulkImageSaving(true);
    try {
      const fd = new FormData();
      bulkImageFiles.forEach((img) => {
        fd.append("images", img);
      });

      const res = await eventAPI.uploadBulkImages(selectedEventForBulk, fd);
      const updated = res.data?.data?.event;
      if (updated) {
        setEvents((prev) =>
          prev.map((e) => (e._id === selectedEventForBulk ? updated : e)),
        );
      }
      toast.success(`${bulkImageFiles.length} image(s) added to event`);
      setBulkImageModal(false);
      setBulkImageFiles([]);
      setBulkImagePreviews([]);
      setSelectedEventForBulk(null);
      triggerRefresh("events");
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to upload images");
    } finally {
      setBulkImageSaving(false);
    }
  };

  const handleToggle = async (id) => {
    try {
      await eventAPI.togglePublish(id);
      setEvents((prev) =>
        prev.map((e) =>
          e._id === id ? { ...e, isPublished: !e.isPublished } : e,
        ),
      );
      toast.success("Event status updated");
      triggerRefresh("events"); // Update all events sections
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to update status");
    }
  };

  const handleDelete = async (id) => {
    try {
      await eventAPI.delete(id);
      setEvents((prev) => prev.filter((e) => e._id !== id));
      setDeleteConfirm(null);
      toast.success("Event deleted");
      triggerRefresh("events"); // Update all events sections
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to delete event");
    }
  };

  const catColors = {
    Academic: "bg-blue-100 text-blue-700",
    Sports: "bg-green-100 text-green-700",
    Cultural: "bg-pink-100 text-pink-700",
    Meeting: "bg-purple-100 text-purple-700",
    Holiday: "bg-orange-100 text-orange-700",
    Other: "bg-gray-100 text-gray-700",
  };

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-serif font-bold text-primary-500">
            Events Management
          </h2>
          <p className="text-gray-500 text-sm mt-1">
            Manage school events and activities
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2.5 bg-primary-500 text-white rounded-xl font-medium hover:bg-primary-600 transition-colors text-sm"
        >
          <FiPlus className="w-4 h-4" /> New Event
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-6">
        <div className="relative">
          <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
          <input
            type="text"
            placeholder="Search events..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
          />
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-100">
                  <th className="text-left px-5 py-3.5 text-gray-500 font-semibold">
                    Event
                  </th>
                  <th className="text-left px-5 py-3.5 text-gray-500 font-semibold hidden sm:table-cell">
                    Category
                  </th>
                  <th className="text-left px-5 py-3.5 text-gray-500 font-semibold hidden md:table-cell">
                    Date
                  </th>
                  <th className="text-left px-5 py-3.5 text-gray-500 font-semibold hidden lg:table-cell">
                    Location
                  </th>
                  <th className="text-left px-5 py-3.5 text-gray-500 font-semibold">
                    Status
                  </th>
                  <th className="text-left px-5 py-3.5 text-gray-500 font-semibold">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((event, idx) => (
                  <motion.tr
                    key={event._id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: idx * 0.05 }}
                    className="border-b border-gray-50 hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-5 py-4">
                      <div className="font-medium text-gray-800">
                        {event.title}
                      </div>
                      <div className="text-xs text-gray-400 max-w-xs truncate">
                        {event.description}
                      </div>
                    </td>
                    <td className="px-5 py-4 hidden sm:table-cell">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-medium ${catColors[event.category] || catColors.Other}`}
                      >
                        {event.category}
                      </span>
                    </td>
                    <td className="px-5 py-4 hidden md:table-cell text-gray-500 text-xs">
                      {formatDate(event.date)}
                    </td>
                    <td className="px-5 py-4 hidden lg:table-cell text-gray-500 text-xs">
                      {event.location || "-"}
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`px-2.5 py-1 rounded-full text-xs font-semibold border ${event.isPublished ? "text-green-700 bg-green-100 border-green-200" : "text-gray-700 bg-gray-100 border-gray-200"}`}
                      >
                        {event.isPublished ? "Published" : "Draft"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-1.5">
                        <button
                          onClick={() => openEdit(event)}
                          className="p-1.5 text-primary-400 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <FiEdit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedEventForBulk(event._id);
                            setBulkImageFiles([]);
                            setBulkImagePreviews([]);
                            setBulkImageModal(true);
                          }}
                          className="p-1.5 text-secondary-400 hover:text-secondary-600 hover:bg-secondary-50 rounded-lg transition-colors"
                          title="Add Photos"
                        >
                          <FiUpload className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleToggle(event._id)}
                          className={`p-1.5 rounded-lg transition-colors ${event.isPublished ? "text-orange-400 hover:text-orange-600 hover:bg-orange-50" : "text-green-400 hover:text-green-600 hover:bg-green-50"}`}
                        >
                          {event.isPublished ? (
                            <FiEyeOff className="w-3.5 h-3.5" />
                          ) : (
                            <FiEye className="w-3.5 h-3.5" />
                          )}
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(event._id)}
                          className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <FiTrash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>
          {filtered.length === 0 && (
            <div className="text-center py-12">
              <FiCalendar className="w-10 h-10 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No events found</p>
            </div>
          )}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Modal
        isOpen={modal}
        onClose={() => setModal(false)}
        title={editItem ? "Edit Event" : "Create Event"}
        size="lg"
        footer={
          <>
            <button
              onClick={() => setModal(false)}
              className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 bg-primary-500 text-white rounded-xl text-sm font-medium hover:bg-primary-600 disabled:opacity-70"
            >
              {saving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : null}
              {editItem ? "Update" : "Create"}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Image upload */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Event Photo
            </label>
            {imagePreview ? (
              <div className="relative w-full h-44 rounded-2xl overflow-hidden border border-gray-200">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="w-full h-full object-cover"
                />
                <button
                  type="button"
                  onClick={() => {
                    setImagePreview(null);
                    setImageFile(null);
                  }}
                  className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                >
                  <FiX className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:border-primary-400 hover:bg-primary-50/30 transition-colors">
                <FiUpload className="w-6 h-6 text-gray-400 mb-1" />
                <span className="text-gray-500 text-sm">
                  Click to upload photo
                </span>
                <span className="text-gray-400 text-xs mt-0.5">
                  JPG, PNG, WebP up to 5MB
                </span>
                <input
                  type="file"
                  onChange={handleImageChange}
                  className="hidden"
                  accept="image/*"
                />
              </label>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Title *
            </label>
            <input
              value={form.title}
              onChange={(e) =>
                setForm((p) => ({ ...p, title: e.target.value }))
              }
              placeholder="Event title"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm((p) => ({ ...p, description: e.target.value }))
              }
              rows={3}
              placeholder="Event description..."
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none resize-none"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Start Date *
              </label>
              <input
                type="date"
                value={form.date}
                onChange={(e) =>
                  setForm((p) => ({ ...p, date: e.target.value }))
                }
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                End Date
              </label>
              <input
                type="date"
                value={form.endDate}
                onChange={(e) =>
                  setForm((p) => ({ ...p, endDate: e.target.value }))
                }
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none"
              />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <input
                value={form.location}
                onChange={(e) =>
                  setForm((p) => ({ ...p, location: e.target.value }))
                }
                placeholder="Venue"
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category
              </label>
              <select
                value={form.category}
                onChange={(e) =>
                  setForm((p) => ({ ...p, category: e.target.value }))
                }
                className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none bg-white"
              >
                {CATEGORIES.map((c) => (
                  <option key={c}>{c}</option>
                ))}
              </select>
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.isPublished}
              onChange={(e) =>
                setForm((p) => ({ ...p, isPublished: e.target.checked }))
              }
              className="w-4 h-4 rounded accent-primary-500"
            />
            <span className="text-sm font-medium text-gray-700">
              Publish immediately (visible on website)
            </span>
          </label>
        </div>
      </Modal>

      <Modal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        title="Confirm Delete"
        size="sm"
        footer={
          <>
            <button
              onClick={() => setDeleteConfirm(null)}
              className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={() => handleDelete(deleteConfirm)}
              className="px-4 py-2 bg-red-500 text-white rounded-xl text-sm font-medium hover:bg-red-600"
            >
              Delete
            </button>
          </>
        }
      >
        <p className="text-gray-600 text-sm">
          Are you sure you want to delete this event? This action cannot be
          undone.
        </p>
      </Modal>

      {/* Bulk Image Upload Modal */}
      <Modal
        isOpen={bulkImageModal}
        onClose={() => setBulkImageModal(false)}
        title="Add Photos to Event"
        size="lg"
        footer={
          <>
            <button
              onClick={() => setBulkImageModal(false)}
              className="px-4 py-2 border border-gray-200 text-gray-600 rounded-xl text-sm hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleBulkImageUpload}
              disabled={bulkImageSaving}
              className="flex items-center gap-2 px-4 py-2 bg-secondary-500 text-white rounded-xl text-sm font-medium hover:bg-secondary-600 disabled:opacity-70"
            >
              {bulkImageSaving ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <FiUpload className="w-4 h-4" />
              )}
              Upload{" "}
              {bulkImageFiles.length > 0 ? `(${bulkImageFiles.length})` : ""}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          {/* Images upload area */}
          <div>
            {bulkImagePreviews.length > 0 ? (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Selected Images ({bulkImagePreviews.length})
                </label>
                <div className="grid grid-cols-3 gap-3 mb-4 max-h-64 overflow-y-auto">
                  {bulkImagePreviews.map((item, idx) => (
                    <div
                      key={idx}
                      className="relative h-24 rounded-lg overflow-hidden border border-gray-200"
                    >
                      <img
                        src={item.preview}
                        alt={`Preview ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                      <button
                        onClick={() => {
                          const newFiles = bulkImageFiles.filter(
                            (_, i) => i !== idx,
                          );
                          const newPreviews = bulkImagePreviews.filter(
                            (_, i) => i !== idx,
                          );
                          setBulkImageFiles(newFiles);
                          setBulkImagePreviews(newPreviews);
                        }}
                        className="absolute top-1 right-1 p-0.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                      >
                        <FiX className="w-2.5 h-2.5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            ) : null}
            <label className="flex flex-col items-center justify-center w-full h-40 border-2 border-dashed border-gray-200 rounded-2xl cursor-pointer hover:border-secondary-400 hover:bg-secondary-50/30 transition-colors">
              <FiUpload className="w-8 h-8 text-gray-400 mb-2" />
              <span className="text-gray-500 text-sm">
                Click to select photos
              </span>
              <span className="text-gray-400 text-xs mt-1">
                JPG, PNG, WebP up to 5MB (max 50 images)
              </span>
              <input
                type="file"
                multiple
                onChange={handleBulkImageChange}
                className="hidden"
                accept="image/*"
              />
            </label>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
            <p className="text-sm text-blue-700">
              💡 <strong>Tip:</strong> Upload multiple event photos at once.
              They will be associated with this event and displayed on the event
              details page.
            </p>
          </div>
        </div>
      </Modal>
    </AdminLayout>
  );
};

export default AdminEvents;
